import { useEffect, useState } from "react";
import { api, resolveSrc, invalidateSiteCache } from "@/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import UploadField from "@/components/admin/UploadField";

const emptyForm = { titre: "", ville: "", prestation: "", mode: "slider", avant: "", apres: "", photo: "", ordre: 0 };

const inputCls = "mt-2 w-full border-b border-bark/40 bg-transparent py-2.5 outline-none transition-[border-color] duration-300 focus:border-clay";

const AdminChantiers = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/chantiers").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, ordre: Number(form.ordre) || 0 };
    if (payload.mode === "photo") {
      payload.avant = null;
      payload.apres = null;
    } else {
      payload.photo = null;
    }
    try {
      if (form.id) {
        await api.put(`/admin/chantiers/${form.id}`, payload);
        toast.success("Réalisation mise à jour");
      } else {
        await api.post("/admin/chantiers", payload);
        toast.success("Réalisation ajoutée");
      }
      invalidateSiteCache();
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer « ${c.titre} » ?`)) return;
    await api.delete(`/admin/chantiers/${c.id}`);
    toast.success("Réalisation supprimée");
    invalidateSiteCache();
    load();
  };

  if (form) {
    return (
      <form onSubmit={save} className="max-w-2xl space-y-7" data-testid="admin-chantier-form">
        <h2 className="font-serif text-2xl tracking-tight">{form.id ? "Modifier" : "Nouvelle"} réalisation</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-taupe">Titre</label>
            <input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className={inputCls} data-testid="chantier-input-titre" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-taupe">Ville</label>
            <input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className={inputCls} data-testid="chantier-input-ville" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-taupe">Prestation</label>
            <input value={form.prestation} onChange={(e) => setForm({ ...form, prestation: e.target.value })} className={inputCls} data-testid="chantier-input-prestation" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-taupe">Ordre d'affichage</label>
            <input type="number" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} className={inputCls} data-testid="chantier-input-ordre" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-taupe">Format</label>
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={`${inputCls} cursor-pointer`} data-testid="chantier-select-mode">
            <option value="slider">Comparateur avant / après</option>
            <option value="photo">Photo unique</option>
          </select>
        </div>
        {form.mode === "slider" ? (
          <div className="grid gap-6 sm:grid-cols-2">
            <UploadField label="Photo avant" value={form.avant} onChange={(v) => setForm({ ...form, avant: v })} testid="chantier-upload-avant" />
            <UploadField label="Photo après" value={form.apres} onChange={(v) => setForm({ ...form, apres: v })} testid="chantier-upload-apres" />
          </div>
        ) : (
          <UploadField label="Photo" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testid="chantier-upload-photo" />
        )}
        <div className="flex gap-4">
          <button type="submit" data-testid="chantier-save" className="rounded-full bg-clay px-8 py-3.5 text-sm font-medium text-cream transition-colors duration-300 hover:bg-ink">
            Enregistrer
          </button>
          <button type="button" onClick={() => setForm(null)} data-testid="chantier-cancel" className="rounded-full border border-ink/20 px-8 py-3.5 text-sm text-bark hover:border-clay hover:text-clay">
            Annuler
          </button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="admin-chantiers-list">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl tracking-tight">Réalisations ({items.length})</h2>
        <button onClick={() => setForm({ ...emptyForm, ordre: items.length })} data-testid="chantier-add" className="flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-medium text-cream hover:bg-ink">
          <Plus size={15} /> Ajouter
        </button>
      </div>
      <div className="mt-8 space-y-4">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-5 bg-white/60 p-4" data-testid={`admin-chantier-${c.id}`}>
            <img src={resolveSrc(c.apres || c.photo || c.image)} alt={c.titre} className="h-16 w-24 shrink-0 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{c.titre}</p>
              <p className="text-sm text-taupe">
                {c.ville || "—"} · {c.mode === "slider" ? "Avant / après" : "Photo unique"}
              </p>
            </div>
            <button onClick={() => setForm({ ...c })} data-testid={`chantier-edit-${c.id}`} className="p-2 text-bark hover:text-clay" aria-label={`Modifier ${c.titre}`}>
              <Pencil size={17} />
            </button>
            <button onClick={() => remove(c)} data-testid={`chantier-delete-${c.id}`} className="p-2 text-bark hover:text-clay" aria-label={`Supprimer ${c.titre}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminChantiers;
